import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MAX_PHOTO_LABELS_PER_ORG, type PhotoLabel } from "@rebuild/shared";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { RowSkeleton } from "@/components/skeleton/RowSkeleton";
import { useDebounceState } from "@/hooks/useDebounceState";
import { SearchInput } from "@/pages/dashboard/SearchInput";
import { createColumnHelper } from "@tanstack/react-table";
import { appActions } from "@/redux/actions/app.actions";
import { appSelector } from "@/redux/selectors/app.selector";
import { useDispatch, useSelector } from "@/redux/store";
import { SETTINGS_TABLE_WRAPPER } from "@/pages/settings/settings-layout-constants";
import { SettingsTableToolbar } from "@/pages/settings/SettingsTableToolbar";
import { UnsavedChangesBar } from "@/components/settings/UnsavedChangesBar";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { Plus, Trash2 } from "lucide-react";
import AuthProvider from "@/providers/auth.provider";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { AddPhotoLabelDialog } from "./AddPhotoLabelDialog";
import { InlineEditCell } from "./InlineEditCell";

const DEFAULT_PHOTO_LABELS_PAGE_SIZE = 25;

function createPhotoLabelId() {
  return uuidv4();
}

function validateLabelsForSave(labels: PhotoLabel[]): string | null {
  const trimmed = labels.map((label) => label.name.trim());
  if (trimmed.some((name) => name.length === 0)) {
    return "Each label must have a name.";
  }

  const normalized = trimmed.map((name) => name.toLowerCase());
  if (new Set(normalized).size !== normalized.length) {
    return "Label names must be unique.";
  }

  if (labels.length > MAX_PHOTO_LABELS_PER_ORG) {
    return `You can save at most ${MAX_PHOTO_LABELS_PER_ORG} labels.`;
  }

  return null;
}

const labelColumnHelper = createColumnHelper<PhotoLabel>();

export function PhotoLabelsSettingsPage() {
  const dispatch = useDispatch();
  const user = useSelector(appSelector.user);
  const serverLabels = useSelector(appSelector.organizationPhotoLabels);
  const serverLabelsSerialized = useMemo(() => JSON.stringify(serverLabels), [serverLabels]);

  const [labelRows, setLabelRows] = useState<PhotoLabel[]>([]);
  const [deleteRow, setDeleteRow] = useState<PhotoLabel | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [immediateSaving, setImmediateSaving] = useState(false);
  const [dirtyTrackingReady, setDirtyTrackingReady] = useState(false);
  // Guards the server-sync effect after an add/delete refetch, so it can't clobber unsaved renames.
  const skipNextServerSyncRef = useRef(false);

  const [labelSearch, setLabelSearch] = useState("");
  const debouncedLabelSearch = useDebounceState(labelSearch, 300);
  const [labelsPageSize, setLabelsPageSize] = useState(DEFAULT_PHOTO_LABELS_PAGE_SIZE);

  const refetchUserDetails = useCallback(async () => {
    const firebaseUser = getAuth().currentUser;
    if (!firebaseUser) return;
    const firebaseToken = await firebaseUser.getIdToken(false);
    await dispatch(appActions.getUserDetails(firebaseUser, firebaseToken));
  }, [dispatch]);

  useEffect(() => {
    if (skipNextServerSyncRef.current) {
      skipNextServerSyncRef.current = false;
      setDirtyTrackingReady(true);
      return;
    }
    setLabelRows(serverLabels.map((label) => ({ ...label })));
    setDirtyTrackingReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serialized dep prevents re-sync on reference-only changes
  }, [serverLabelsSerialized]);

  const handleRenameLabel = useCallback((rowId: string, value: string) => {
    setLabelRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, name: value } : row)));
  }, []);

  const saveLabels = useCallback(
    async (nextLabels: PhotoLabel[]): Promise<boolean> => {
      const orgId = user?.organizationId;
      if (!orgId) {
        toast.error("Missing organization.");
        return false;
      }

      const validationError = validateLabelsForSave(nextLabels);
      if (validationError) {
        toast.error(validationError);
        return false;
      }

      try {
        const res = await AuthProvider.editOrganization({
          organizationId: orgId,
          customSettings: JSON.stringify({
            photoLabels: nextLabels.map((label) => ({ id: label.id, name: label.name.trim() })),
          }),
        });
        if (!res.success) {
          toast.error(res.error ?? "Could not save changes.");
          return false;
        }
        return true;
      } catch (e) {
        console.error(e);
        toast.error("Could not save changes.");
        return false;
      }
    },
    [user?.organizationId],
  );

  // Writes the server baseline, not the draft: pending renames stay local until Save changes.
  const saveLabelsImmediate = useCallback(
    async (nextLabels: PhotoLabel[]): Promise<boolean> => {
      setImmediateSaving(true);
      try {
        const ok = await saveLabels(nextLabels);
        if (!ok) return false;
        skipNextServerSyncRef.current = true;
        await refetchUserDetails();
        return true;
      } finally {
        setImmediateSaving(false);
      }
    },
    [refetchUserDetails, saveLabels],
  );

  const handleConfirmAdd = useCallback(
    async (name: string) => {
      const newLabel: PhotoLabel = { id: createPhotoLabelId(), name };
      const ok = await saveLabelsImmediate([...serverLabels, newLabel]);
      if (!ok) return;

      setLabelRows((prev) => [...prev, newLabel]);
      setAddDialogOpen(false);
      toast.success("Label added.");
    },
    [saveLabelsImmediate, serverLabels],
  );

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;

    const existsOnServer = serverLabels.some((label) => label.id === deleteRow.id);
    if (!existsOnServer) {
      setLabelRows((prev) => prev.filter((row) => row.id !== deleteRow.id));
      setDeleteRow(null);
      return;
    }

    const ok = await saveLabelsImmediate(serverLabels.filter((label) => label.id !== deleteRow.id));
    if (!ok) return;

    setLabelRows((prev) => prev.filter((row) => row.id !== deleteRow.id));
    setDeleteRow(null);
    toast.success("Label deleted.");
  };

  const onDiscard = useCallback(() => {
    setLabelRows(serverLabels.map((label) => ({ ...label })));
  }, [serverLabels]);

  const onSave = async () => {
    setSaving(true);
    try {
      const ok = await saveLabels(labelRows);
      if (!ok) return;
      toast.success("Saved.");
      await refetchUserDetails();
    } finally {
      setSaving(false);
    }
  };

  const serverBaselineFingerprint = useMemo(
    () => JSON.stringify(serverLabels.map((label) => ({ id: label.id, name: label.name.trim() }))),
    [serverLabels],
  );

  const draftFingerprint = useMemo(
    () => JSON.stringify(labelRows.map((label) => ({ id: label.id, name: label.name.trim() }))),
    [labelRows],
  );

  const isDirty = dirtyTrackingReady && draftFingerprint !== serverBaselineFingerprint;

  const filteredLabelRows = useMemo(() => {
    const q = debouncedLabelSearch.trim().toLowerCase();
    if (!q) return labelRows;
    return labelRows.filter((row) => row.name.toLowerCase().includes(q));
  }, [labelRows, debouncedLabelSearch]);

  const labelColumns = useMemo(
    () => [
      labelColumnHelper.display({
        id: "name",
        header: "Label",
        cell: ({ row }) => (
          <InlineEditCell
            rowId={row.original.id}
            displayValue={row.original.name}
            onSave={handleRenameLabel}
            ariaLabel="Label name"
            placeholder="Click to add name"
            title="Click to edit label"
          />
        ),
        meta: { bodyClassName: "!w-[26rem] !min-w-[26rem] !max-w-[26rem]" },
      }),
      labelColumnHelper.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex">
            <Button type="button" variant="ghost" size="icon" aria-label="Delete label" onClick={() => setDeleteRow(row.original)}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        ),
        meta: { bodyClassName: "!w-20 !min-w-20 !max-w-20" },
      }),
    ],
    [handleRenameLabel],
  );

  const openAddDialog = () => {
    setLabelSearch("");
    setAddDialogOpen(true);
  };

  const labelsTableToolbar = (
    <SettingsTableToolbar>
      <SearchInput
        value={labelSearch}
        onSearch={setLabelSearch}
        placeholder="Search labels…"
        wrapperClassName="min-w-0 flex-1 basis-[12rem] max-w-md shrink"
      />
      <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1 sm:ml-auto" onClick={openAddDialog}>
        <Plus className="size-4" />
        Add label
      </Button>
    </SettingsTableToolbar>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto pb-20">
      <div className="border-border flex flex-1 flex-col gap-6">
        <div className="flex w-full flex-col gap-2">
          <h2 className="text-foreground text-base font-semibold">Photo labels</h2>

          {labelRows.length === 0 ? (
            <div className={SETTINGS_TABLE_WRAPPER}>
              <div className="flex flex-col items-start gap-3 p-6">
                <p className="text-muted-foreground max-w-xl text-sm">
                  Build a shared list of labels for your team to pick from when labelling photos.
                </p>
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={openAddDialog}>
                  <Plus className="size-4" />
                  Add label
                </Button>
              </div>
            </div>
          ) : (
            <div className={cn(SETTINGS_TABLE_WRAPPER, "flex flex-col overflow-hidden p-0")}>
              {labelsTableToolbar}
              <DataTable<PhotoLabel>
                columns={labelColumns}
                data={filteredLabelRows}
                embedded
                isLoading={false}
                pageSize={labelsPageSize}
                onPageSizeChange={setLabelsPageSize}
                renderSkeleton={() => <RowSkeleton />}
                emptyState={<span className="text-muted-foreground text-sm">No labels match your search. Try another name.</span>}
              />
            </div>
          )}
        </div>
      </div>

      <AddPhotoLabelDialog
        open={addDialogOpen}
        existingNames={labelRows.map((row) => row.name)}
        isSaving={immediateSaving}
        onCancel={() => setAddDialogOpen(false)}
        onConfirm={handleConfirmAdd}
      />

      <AlertDialog open={!!deleteRow} onOpenChange={(open) => !open && !immediateSaving && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete label?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow
                ? `This will remove "${deleteRow.name.trim() || "Untitled label"}" from the list your team can pick from. Photos already labelled with it keep their label. This can't be undone.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={immediateSaving}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="border-destructive/40"
              onClick={() => void handleConfirmDelete()}
              disabled={immediateSaving}
            >
              {immediateSaving ? "Deleting…" : "Delete label"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedChangesBar isDirty={isDirty} isSaving={saving} onSave={() => void onSave()} onDiscard={onDiscard} />
    </div>
  );
}
